import React, { useState, useEffect } from "react";
import API from "api/endpoints";
import { Button, Card, Empty, message, Space, Typography, Select, Tag, Popconfirm } from "antd";
import axiosInstance from "api/axiosInstance";
import { LeaveRequestEnum, LeaveRequestEnumLabel } from "Constants/Enums/LeaveRequestEnum";

const { Title, Text } = Typography;
const { Option } = Select;

export default function LeaveRequest() {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [cancelingId, setCancelingId] = useState(null); // id of the request currently being force-cancelled

  useEffect(() => {
    fetchLeaveRequests();
    fetchDepartments();
  }, []);

  // Fetch all departments
  async function fetchDepartments() {
    setDepartmentsLoading(true);
    try {
      const response = await axiosInstance.get(API.department.getList);
      setDepartments(response.data);
    } catch (error) {
      console.error("Error fetching departments:", error);
      message.error("Failed to fetch departments");
    } finally {
      setDepartmentsLoading(false);
    }
  }

  // Fetch leave requests with optional department filter
  async function fetchLeaveRequests(departmentId = null) {
    setLoading(true);
    try {
      // HR/Admin now sees the full leave list as a report (approval moved to
      // the supervisor). Department filtering is applied client-side below.
      const response = await axiosInstance.get(API.leaveRequest.getAll);
      setLeaveRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(" Error fetching leave requests:", error);
      message.error("Failed to fetch leave requests");
    } finally {
      setLoading(false);
    }
  }

  // Filter leave requests based on selected department (client-side filtering)
  const getFilteredLeaveRequests = () => {
    if (!selectedDepartment) {
      return leaveRequests;
    }

    return leaveRequests.filter((request) => {
      const userDepartment = request.departmentId || request.employee?.departmentId;
      return (
        userDepartment === selectedDepartment ||
        request.employee?.departmentId === selectedDepartment
      );
    });
  };

  // Handle department change
  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
  };

  // HR/Admin force-cancel (admin correction). Approval/rejection now belongs to
  // the supervisor/manager, so HR keeps only this corrective action.
  const handleAdminCancel = async (requestId) => {
    try {
      setCancelingId(requestId);
      // The endpoint expects a JSON string body (a cancellation note). A bodyless
      // PUT was being rejected even with a valid id, which is why this kept failing.
      await axiosInstance.put(
        API.leaveRequest.adminCancel(requestId),
        JSON.stringify("Cancelled by HR"),
        { headers: { "Content-Type": "application/json" } }
      );
      message.success("Leave request cancelled");
      await fetchLeaveRequests(selectedDepartment);
    } catch (error) {
      console.error("Error cancelling leave request:", error, "id:", requestId);
      const status = error.response?.status;
      const detail =
        error.response?.data?.message ||
        error.response?.data?.details ||
        error.response?.data?.title;
      message.error(
        `Failed to cancel leave request${status ? ` (${status})` : ""}${
          detail ? `: ${detail}` : ""
        }`
      );
    } finally {
      setCancelingId(null);
    }
  };

  // Using your enums for status display
  const getStatusTag = (status) => {
    const colorMap = {
      [LeaveRequestEnum.Pending]: "orange",
      [LeaveRequestEnum.Approved]: "green",
      [LeaveRequestEnum.Rejected]: "red",
      [LeaveRequestEnum.Cancelled]: "gray",
    };

    return (
      <Tag color={colorMap[status] || "default"}>{LeaveRequestEnumLabel[status] || "Unknown"}</Tag>
    );
  };

  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
  };

  const filteredRequests = getFilteredLeaveRequests();

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>Leave Requests</span>
          <Select
            placeholder="All Departments"
            style={{ width: 250 }}
            onChange={handleDepartmentChange}
            value={selectedDepartment}
            allowClear
            onClear={() => {
              setSelectedDepartment(null);
              fetchLeaveRequests();
            }}
            loading={departmentsLoading}
            disabled={departmentsLoading}
          >
            <Option value={null}>All Departments</Option>
            {departments.map((dept) => (
              <Option key={dept.id || dept.departmentId} value={dept.id || dept.departmentId}>
                {dept.name || dept.departmentName}
              </Option>
            ))}
          </Select>
        </div>
      }
      className="card-professional"
      loading={loading}
      extra={
        selectedDepartment && (
          <Tag color="blue" style={{ marginRight: "10px" }}>
            Showing:{" "}
            {departments.find((d) => (d.id || d.departmentId) === selectedDepartment)?.name ||
              selectedDepartment}
          </Tag>
        )
      }
    >
      {filteredRequests.length > 0 ? (
        <div className="row">
          {filteredRequests.map((request) => (
            <div key={request.leaveRequestId ?? request.id} className="col-md-6">
              <Card
                style={{ marginBottom: 15 }}
                size="small"
                styles={{ body: { padding: "16px" } }}
              >
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  {/* Department Badge */}
                  {(request.department || request.employee?.departmentName) && (
                    <div style={{ textAlign: "right" }}>
                      <Tag color="blue">Dept: {request.employee?.departmentName}</Tag>
                    </div>
                  )}

                  {/* Employee Information */}
                  <Space style={rowStyle}>
                    <Text strong>Employee:</Text>
                    <Text>
                      {request.employee?.firstName} {request.employee?.lastName}
                    </Text>
                  </Space>

                  {/* Leave Type */}
                  <Space style={rowStyle}>
                    <Text strong>Leave Type:</Text>
                    <Tag color="purple">{request.leavePolicy?.policyName}</Tag>
                  </Space>

                  {/* Duration */}
                  <Space style={rowStyle}>
                    <Text strong>Duration:</Text>
                    <Text>{request.amountDays || request.totalDays} Days</Text>
                  </Space>

                  {/* Paid / Unpaid split (Point 16) */}
                  {(request.paidDays !== undefined || request.unpaidDays !== undefined) && (
                    <Space style={rowStyle}>
                      <Text strong>Paid / Unpaid:</Text>
                      <span>
                        <Tag color="green">{request.paidDays ?? 0} paid</Tag>
                        {Number(request.unpaidDays) > 0 && (
                          <Tag color="red">{request.unpaidDays} unpaid</Tag>
                        )}
                      </span>
                    </Space>
                  )}

                  {/* Date Range */}
                  <Space style={rowStyle}>
                    <Text strong>Date Range:</Text>
                    <Text>
                      {request.fromDate} to {request.toDate}
                    </Text>
                  </Space>

                  {/* Granted Days */}
                  {request.grantedDays && (
                    <Space style={rowStyle}>
                      <Text strong>Granted Days/Year:</Text>
                      <Text>{request.leavePolicy?.grantedDaysPerYear} Days</Text>
                    </Space>
                  )}

                  {/* Status - USING YOUR ENUMS */}
                  <Space style={rowStyle}>
                    <Text strong>Status:</Text>
                    {getStatusTag(request.status)}
                  </Space>

                  {/* Reason */}
                  {request.reason && (
                    <Space style={rowStyle}>
                      <Text strong>Reason:</Text>
                      <Text style={{ maxWidth: "200px", textAlign: "right" }}>
                        {request.reason}
                      </Text>
                    </Space>
                  )}
                </Space>

                {/* HR/Admin corrective action only. Approval/rejection now
                    belongs to the supervisor. HR can force-cancel a request. */}
                {request.status !== LeaveRequestEnum.Cancelled && (
                  <div
                    style={{
                      marginTop: 16,
                      textAlign: "right",
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: "12px",
                    }}
                  >
                    <Popconfirm
                      title="Cancel this leave request?"
                      description="This is an admin correction and cannot be undone."
                      okText="Yes, cancel"
                      cancelText="No"
                      okButtonProps={{ loading: cancelingId === (request.leaveRequestId ?? request.id) }}
                      onConfirm={() => handleAdminCancel(request.leaveRequestId ?? request.id)}
                    >
                      <Button
                        danger
                        size="small"
                        loading={cancelingId === (request.leaveRequestId ?? request.id)}
                      >
                        Force Cancel
                      </Button>
                    </Popconfirm>
                  </div>
                )}
              </Card>
            </div>
          ))}
        </div>
      ) : (
        <Empty
          description={
            selectedDepartment
              ? `No leave requests in ${
                  departments.find((d) => (d.id || d.departmentId) === selectedDepartment)?.name ||
                  selectedDepartment
                } department`
              : "No leave requests available"
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* Summary Stats */}
      {filteredRequests.length > 0 && (
        <div style={{ marginTop: 16, padding: "12px", background: "#f5f5f5", borderRadius: "6px" }}>
          <Text type="secondary">
            Showing {filteredRequests.length} request{filteredRequests.length !== 1 ? "s" : ""}
            {selectedDepartment &&
              ` in ${
                departments.find((d) => (d.id || d.departmentId) === selectedDepartment)?.name ||
                selectedDepartment
              } department`}
          </Text>
        </div>
      )}
    </Card>
  );
}
